import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Close from "@mui/icons-material/Close";
import { pageButtonCancelProps, pageButtonDangerProps } from "../../lib/ui/pageButtons";
import { useTranslation } from "react-i18next";

export type ConfirmOptions = {
  title?: ReactNode;
  description?: ReactNode;
  confirmationText?: string;
  cancellationText?: string;
  confirmationButtonProps?: Record<string, unknown>;
  cancellationButtonProps?: Record<string, unknown>;
  dialogProps?: Record<string, unknown>;
};

type ConfirmFn = (options?: ConfirmOptions) => Promise<void>;

const ConfirmContext = createContext<{ confirm: ConfirmFn } | null>(null);

export function ConfirmProvider({
  children,
  defaultOptions,
}: {
  children: ReactNode;
  defaultOptions?: ConfirmOptions;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions>({});
  const resolverRef = useRef<{
    resolve: () => void;
    reject: (reason?: unknown) => void;
  } | null>(null);

  const close = useCallback((confirmed: boolean) => {
    setOpen(false);
    const pending = resolverRef.current;
    resolverRef.current = null;
    if (!pending) return;
    if (confirmed) pending.resolve();
    else pending.reject();
  }, []);

  const confirm = useCallback<ConfirmFn>(
    (nextOptions = {}) =>
      new Promise<void>((resolve, reject) => {
        resolverRef.current?.reject();
        resolverRef.current = { resolve, reject };
        setOptions({ ...defaultOptions, ...nextOptions });
        setOpen(true);
      }),
    [defaultOptions],
  );

  const value = useMemo(() => ({ confirm }), [confirm]);
  const title = options.title ?? defaultOptions?.title ?? t("common.areYouSure");
  const confirmationText = options.confirmationText ?? defaultOptions?.confirmationText ?? t("common.confirm");
  const cancellationText = options.cancellationText ?? defaultOptions?.cancellationText ?? t("common.cancel");
  const dialogProps = {
    maxWidth: "xs" as const,
    fullWidth: true,
    ...(defaultOptions?.dialogProps ?? {}),
    ...(options.dialogProps ?? {}),
  };

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <Dialog
        open={open}
        onClose={() => close(false)}
        {...dialogProps}
      >
        <DialogTitle sx={{ position: "relative", display: "flex", alignItems: "center", pr: 6 }}>
          {title}
          <IconButton
            aria-label={cancellationText}
            onClick={() => close(false)}
            size="small"
            sx={{ position: "absolute", right: 12, top: 12 }}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {typeof options.description === "string" ? (
            <Typography variant="body2" color="text.secondary">
              {options.description}
            </Typography>
          ) : (
            options.description
          )}
        </DialogContent>
        <DialogActions>
          <Button
            {...pageButtonCancelProps}
            type="button"
            onClick={() => close(false)}
            {...(options.cancellationButtonProps as object)}
          >
            {cancellationText}
          </Button>
          <Button
            {...pageButtonDangerProps}
            type="button"
            onClick={() => close(true)}
            {...(options.confirmationButtonProps as object)}
          >
            {confirmationText}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm must be used within ConfirmProvider");
  }
  return ctx.confirm;
}
